import CloseIcon from "@mui/icons-material/Close";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, CircularProgress, Modal } from "@mui/material";
import { useContext, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { UserContext } from "../../../../App";
import useGetDevice from "../../product/hooks/useGetDevice";
import { useSnackbar } from "../../product/hooks/SnackbarProvider";
import "./OrderIdValidationModal.css";
import { getManagers, validateManagerPin } from "../../../../config/Services";
import { getFromLocalStorage } from "../../../../utils/localStorageHelpers";
import StyledSelect from "../../product/all-products/components/custom/StyledSelect";

export const OrderIdValidationModal = (props) => {
  const { color } = useContext(UserContext);
  const isMobile = useGetDevice();
  const showSnackbar = useSnackbar();
  const locId = getFromLocalStorage("locId");

  const [selectedManager, setSelectedManager] = useState("");
  const [managerPin, setManagerPin] = useState("");
  const [isPinVisible, setIsPinVisible] = useState(false);
  const [errors, setErrors] = useState({ manager: "", pin: "", orderId: "" });

  const isDark = color === "dark";

  const handleClose = () => {
    setSelectedManager("");
    setManagerPin("");
    setErrors({ manager: "", pin: "", orderId: "" });
    props?.onClose();
  };

  const { data: managerList = [], isLoading: isManagerListLoading } = useQuery({
    refetchOnWindowFocus: false,
    queryKey: ["managers"],
    queryFn: async () => {
      const payload = { contextId: locId };
      const response = await getManagers(payload);
      return response?.data?.data?.map((e) => ({
        value: e?.mobile_number,
        label: `${e?.first_name} · ${e?.role || "Manager"}`,
        uidx: e?.uidx,
      }));
    },
    enabled: props?.open,
    staleTime: 5 * 60 * 1000,
    onError: (error) => {
      showSnackbar(error?.response?.data?.message || error?.message, "error");
    },
  });

  const { mutate: validateManager, isLoading: isValidating } = useMutation({
    mutationKey: "validateManager",
    mutationFn: async (payload) => {
      const response = await validateManagerPin(payload);
      if (response?.data?.data?.status === "ERROR") {
        throw new Error(response?.data?.data?.message);
      }
      return response;
    },
    onSuccess: () => {
      props?.handleReturnNavigate();
    },
    onError: (error) => {
      const msg = error?.response?.data?.message || error?.message || "Invalid PIN";
      setErrors((prev) => ({ ...prev, pin: msg }));
    },
  });

  const validate = () => {
    const newErrors = { manager: "", pin: "", orderId: "" };
    let valid = true;
    if (!selectedManager) {
      newErrors.manager = "Select a manager";
      valid = false;
    }
    if (managerPin?.length !== 4) {
      newErrors.pin = "Enter a valid 4-digit manager PIN";
      valid = false;
    }
    if (!props?.orderIdVal || props?.orderIdVal?.trim()?.length === 0) {
      newErrors.orderId = "Enter an order ID";
      valid = false;
    }
    setErrors(newErrors);
    return valid;
  };

  const handleProceed = () => {
    if (!validate()) return;
    validateManager({ mobile: selectedManager, primaryPassword: managerPin });
  };

  return (
    <Modal
      open={props.open}
      onClose={handleClose}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(0,0,0,0.35)", backdropFilter: "blur(4px)" },
        },
      }}
    >
      <Box className={`return-modal-container ${isDark ? "return-modal-dark" : ""}`}>
        <header className="return-modal-header">
          <h2 className="return-modal-title">New Return</h2>
          <button className="return-modal-close" onClick={handleClose} aria-label="Close dialog">
            <CloseIcon fontSize="small" />
          </button>
        </header>

        <div className="return-modal-body">
          <div className="return-form-field">
            <label htmlFor="return-manager" className="return-form-label">
              Manager
            </label>
            <div className={`return-select-wrapper ${errors?.manager ? "return-select-error" : ""}`}>
              <StyledSelect
                placeholder={isManagerListLoading ? "Loading managers..." : "Select manager"}
                options={managerList}
                value={managerList?.find((mgr) => mgr?.value === selectedManager) || null}
                onChange={(option) => {
                  setSelectedManager(option ? option?.value : "");
                  setErrors((prev) => ({ ...prev, manager: "" }));
                }}
                error={!!errors?.manager}
                isLoading={isManagerListLoading}
                size="large"
              />
            </div>
            {errors?.manager && (
              <p className="return-field-error" role="alert">
                {errors?.manager}
              </p>
            )}
          </div>

          <div className="return-form-row">
            <div className="return-form-field">
              <label htmlFor="return-manager-pin" className="return-form-label">
                Manager PIN
              </label>
              <div className="return-pin-wrapper">
                <input
                  id="return-manager-pin"
                  type={isPinVisible ? "text" : "password"}
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="Enter 4-digit PIN"
                  className={`return-input ${errors?.pin ? "return-input-error" : ""}`}
                  value={managerPin}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 4);
                    setManagerPin(val);
                    setErrors((prev) => ({ ...prev, pin: "" }));
                  }}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="return-pin-toggle"
                  onClick={() => setIsPinVisible((v) => !v)}
                  aria-label={isPinVisible ? "Hide PIN" : "Show PIN"}
                >
                  {isPinVisible ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </button>
              </div>
              {errors?.pin && (
                <p className="return-field-error" role="alert">
                  {errors?.pin}
                </p>
              )}
            </div>

            <div className="return-form-field">
              <label htmlFor="return-order-id" className="return-form-label">
                Order ID
              </label>
              <input
                id="return-order-id"
                type="text"
                placeholder="Enter order ID"
                className={`return-input ${errors?.orderId ? "return-input-error" : ""}`}
                value={props?.orderIdVal}
                onChange={(e) => {
                  props?.setOrderIdVal(e.target.value);
                  setErrors((prev) => ({ ...prev, orderId: "" }));
                }}
              />
              {errors?.orderId && (
                <p className="return-field-error" role="alert">
                  {errors?.orderId}
                </p>
              )}
            </div>
          </div>
        </div>

        <footer className="return-modal-footer">
          <button type="button" className="return-btn return-btn-cancel" onClick={handleClose}>
            Cancel
          </button>
          <button
            type="button"
            className="return-btn return-btn-proceed"
            onClick={handleProceed}
            disabled={isValidating || props?.loader}
          >
            {isValidating || props?.loader ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : (
              "Proceed to return"
            )}
          </button>
        </footer>
      </Box>
    </Modal>
  );
};
